import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Typography,
  Space,
  Divider,
  Row,
  Col,
  message,
  Tag,
  Tooltip,
  Checkbox,
  Switch,
  Popconfirm,
} from "antd";
import { BankOutlined, BulbOutlined, SaveOutlined, SettingOutlined, CheckOutlined, InfoCircleOutlined, BranchesOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import PhoneInput from "react-phone-input-2";
import {
  isPossiblePhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input";
import "react-phone-input-2/lib/style.css";
import { useAppDispatch, useAppSelector } from "../../../hooks/reduxHooks";
import {
  getBusinessInfoApi,
  getTenantLanguageListApi,
  getTenantPresetDataApi,
  getTenantToolsApi,
  editBusinessInfoApi,
  createCustomToolApi,
  getCustomToolsApi,
  deleteCustomToolApi,
  updateCustomToolApi,
} from "../../../services/tenantSetup.service";
import AISettingsComponent from "./AISettingsComponent";
import CustomToolModal from "./CustomToolModal";
import { requiredInfoSuggestions } from "../../../data/requiredInfoSuggestions";
import UpgradeDialog from "../../../components/common/UpgradeDialog";
// import "./AISetupComponent.scss";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option, OptGroup } = Select;

const AISetupComponent = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState({
    businessInfo: false,
    presetData: false,
    languages: false,
    tools: false
  });
  const [savingOrgInfo, setSavingOrgInfo] = useState(false);
  const [savingAgentSetup, setSavingAgentSetup] = useState(false);
  const [savingCtaConfig, setSavingCtaConfig] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [agentGoalTypes, setAgentGoalTypes] = useState([]);
  const [responseStyles, setResponseStyles] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [toolsList, setToolsList] = useState([]);
  const [tenantTools, setTenantTools] = useState([]);
  const [customTools, setCustomTools] = useState([]);
  const [phoneInputValue, setPhoneInputValue] = useState("");
  const [primaryLanguage, setPrimaryLanguage] = useState(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showCustomToolModal, setShowCustomToolModal] = useState(false);
  const [creatingCustomTool, setCreatingCustomTool] = useState(false);
  const [editingCustomTool, setEditingCustomTool] = useState(false);
  const [customToolMode, setCustomToolMode] = useState("create");
  const [editingToolId, setEditingToolId] = useState(null);
  const dispatch = useAppDispatch();
  const { isPaidPlan } = useAppSelector((state) => state.currentPlan);

  const getCanonicalToolKey = (toolName) => {
    const tool = toolsList.find((item) =>
      item.issue_type === toolName || item.name === toolName || item.label === toolName
    );

    return tool ? tool.issue_type || tool.name : toolName;
  };

  const normalizeCustomTool = (tool) => {
    const toolKey = tool.issue_type || tool.tool_name || tool.name;
    return {
      ...tool,
      name: toolKey,
      issue_type: toolKey,
      label: tool.label || tool.tool_name || toolKey,
      description: tool.description || tool.tool_description || tool.tool_usage_description,
      default: false,
      custom: true,
      webhook_params: tool.webhook_params || [],
      required_fields: tool.required_fields || [],
    };
  };

  const fetchCustomTools = async () => {
    await getCustomToolsApi({
      finalCallback: () => { },
      successCallback: (response) => {
        const tools = Array.isArray(response) ? response.map(normalizeCustomTool) : [];
        setCustomTools(tools);
      },
      failureCallback: () => {
        setCustomTools([]);
      },
    })();
  };

  // Utility function to parse phone number and extract country code
  const parsePhoneNumberData = (phoneNumber) => {
    if (!phoneNumber) {
      return {
        phone_input: "",
        country_code: "",
        phone_number: "",
      };
    }

    try {
      // If the phone number doesn't start with +, add it
      const formattedPhone = phoneNumber.startsWith("+")
        ? phoneNumber
        : `+${phoneNumber}`;

      // Parse the phone number
      const parsed = parsePhoneNumber(formattedPhone);

      if (parsed && parsed.country && parsed.nationalNumber) {
        return {
          phone_input: phoneNumber, // Keep original format for PhoneInput
          country_code: `+${parsed.countryCallingCode}`,
          phone_number: parsed.nationalNumber,
        };
      }
    } catch (error) {
      console.log("Error parsing phone number:", error);
    }

    // Fallback - return the original number as phone_input
    return {
      phone_input: phoneNumber,
      country_code: "",
      phone_number: phoneNumber.replace(/^\+/, ""), // Remove + if present for fallback
    };
  };

  useEffect(() => {
    const mergedByKey = new Map();
    [...tenantTools, ...customTools].forEach((tool) => {
      const key = tool.issue_type || tool.name || tool.label;
      if (key) {
        mergedByKey.set(key, tool);
      }
    });
    setToolsList(Array.from(mergedByKey.values()));
  }, [tenantTools, customTools]);

  // Fetch organization types, languages, and tools
  useEffect(() => {
    (async () => {
      // Fetch Preset Data (organization types, agent goal types, response styles)
      await getTenantPresetDataApi({
        finalCallback: () => { },
        successCallback: (response) => {
          console.log("Preset Data:", response);
          setAgentGoalTypes(response.agent_goal_types || []);
          setResponseStyles(response.response_styles || []);
          setDataLoaded(prev => ({ ...prev, presetData: true }));
        },
        failureCallback: () => {
          setDataLoaded(prev => ({ ...prev, presetData: true }));
        },
      })();

      // Fetch Languages
      await getTenantLanguageListApi({
        finalCallback: () => { },
        successCallback: (response) => {
          console.log("Languages:", response);
          setLanguages(response);
          setDataLoaded(prev => ({ ...prev, languages: true }));
        },
        failureCallback: () => {
          setDataLoaded(prev => ({ ...prev, languages: true }));
        },
      })();

      // Fetch Tools (CTA options)
      await getTenantToolsApi({
        finalCallback: () => { },
        successCallback: (response) => {
          console.log("Tools API Response:", response);
          // Response is always an array of tools
          const tools = Array.isArray(response) ? response : [];
          console.log("Final tools list:", tools);
          setTenantTools(tools);
          setDataLoaded(prev => ({ ...prev, tools: true }));
        },
        failureCallback: () => {
          setDataLoaded(prev => ({ ...prev, tools: true }));
        },
      })();

      await fetchCustomTools();
    })();
  }, []);

  // Normalize any legacy selected values (labels or names) to the canonical key (issue_type || name)
  useEffect(() => {
    const allTools = toolsList || [];
    if (!allTools.length) return;
    const labelToKey = new Map(allTools.map(t => [t.label, t.issue_type || t.name]));
    const nameToKey = new Map(allTools.map(t => [t.name, t.issue_type || t.name]));

    const normalizeArray = (arr) => {
      if (!Array.isArray(arr)) return [];
      return arr.map(v => {
        if (labelToKey.has(v)) return labelToKey.get(v);
        if (nameToKey.has(v)) return nameToKey.get(v);
        return v;
      });
    };

    const currentTools = form.getFieldValue('intervention_tools') || [];
    const normTools = normalizeArray(currentTools);
    const changed = JSON.stringify(currentTools) !== JSON.stringify(normTools);
    if (changed) {
      form.setFieldsValue({ intervention_tools: normTools });
    }
  }, [toolsList, form]);

  // Update loading state when all data is loaded
  useEffect(() => {
    console.log("Data loaded status:", dataLoaded);
    const allDataLoaded = Object.values(dataLoaded).every(loaded => loaded);
    console.log("All data loaded:", allDataLoaded);
    if (allDataLoaded) {
      console.log("Setting loading to false");
      setLoading(false);
    }
  }, [dataLoaded]);

  // Migrate dynamic required-info fields from label-based keys to issue_type-based keys
  useEffect(() => {
    const allTools = toolsList || [];
    if (!allTools.length) return;
    const byLabel = new Map(allTools.map(t => [t.label, t.issue_type || t.name]));
    const values = form.getFieldsValue();
    let changed = false;
    Object.keys(values)
      .filter((k) => k.startsWith('tool_required_info_'))
      .forEach((k) => {
        const suffix = k.replace('tool_required_info_', '');
        const targetKey = byLabel.get(suffix);
        if (targetKey && !values[`tool_required_info_${targetKey}`]) {
          form.setFieldValue(`tool_required_info_${targetKey}`, values[k]);
          changed = true;
        }
      });
    if (changed) {
      // Trigger re-render by setting fields (no-op set)
      form.setFieldsValue({});
    }
  }, [toolsList, form]);

  // Function to populate form from API response
  const populateFormFromApi = (apiData) => {
    if (!apiData) return;

    // Parse phone number if it exists
    let phoneData = {};
    if (apiData.contact_number) {
      const phoneNumber = apiData.contact_number.toString();
      phoneData = parsePhoneNumberData(phoneNumber);
      setPhoneInputValue(phoneNumber);
    }

    // Map API response to form fields (matching tenant setup API format)
    const formData = {
      // Organization Information
      org_name: apiData.org_name || "",
      org_description: apiData.org_description || "",
      org_email: apiData.org_email || "",
      ...phoneData,

      // Agent Setup
      agent_name: apiData.agent_name || "AI Bot",
      agent_goal_type: apiData.agent_goal_type || "",
      additional_agent_goal: apiData.additional_agent_goal || "",
      language: apiData.language_to_reply || ["Auto Detect"],
      romanize_reply: apiData.romanize_reply || false,

      // Tools and Response Style (normalize labels -> keys if needed)
      intervention_tools: apiData.selected_tools || [],
      prompt: apiData.response_style || "standard",
    };

    // normalize intervention_tools (map labels to canonical keys)
    const allTools = toolsList || [];
    if (allTools.length && Array.isArray(formData.intervention_tools)) {
      const byLabel = new Map(allTools.map(t => [t.label, t.issue_type || t.name]));
      formData.intervention_tools = formData.intervention_tools.map((t) => byLabel.get(t) || t);
    }

    // Map selected_tools_required_info into dynamic fields for the form
    if (Array.isArray(apiData.selected_tools_required_info)) {
      const allTools = toolsList || [];
      const byLabel = new Map(allTools.map(t => [t.label, t.issue_type || t.name]));
      const suggestionLabelToName = new Map((requiredInfoSuggestions || []).map(s => [s.label, s.name]));
      apiData.selected_tools_required_info.forEach((toolInfo) => {
        // map tool label -> canonical key (issue_type || name) if needed
        const rawKey = toolInfo.tool;
        const keyName = byLabel.get(rawKey) || rawKey;
        const key = `tool_required_info_${keyName}`;
        formData[key] = (toolInfo.required_info || []).map(i => suggestionLabelToName.get(i) || i);
        formData[`tool_reply_message_${keyName}`] = toolInfo.reply_message || "";
        formData[`tool_allow_empty_response_${keyName}`] = toolInfo.allow_empty_response || false;
        formData[`tool_block_reply_until_resolved_${keyName}`] = toolInfo.block_reply_until_resolved || false;
      });
    }

    // Set form values
    console.log("Setting form data:", formData);
    form.setFieldsValue(formData);

    // Set primary language (first language in the array is considered primary)
    const languageArray = apiData.language_to_reply || [];
    if (languageArray.length > 0 && languageArray[0] !== "Auto Detect") {
      setPrimaryLanguage(languageArray[0]);
    }
  };

  // Load business info from API
  useEffect(() => {
    (() => {
      dispatch(
        getBusinessInfoApi({
          finalCallback: () => { },
          successCallback: (response) => {
            console.log("Business Info Response from ai settings:", response);
            populateFormFromApi(response);
            setDataLoaded(prev => ({ ...prev, businessInfo: true }));
          },
          failureCallback: () => {
            setDataLoaded(prev => ({ ...prev, businessInfo: true }));
          },
        })
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, form]);

  const handleSave = async (values) => {
    // Validate phone number only if provided
    if (values.phone_input) {
      const phoneToValidate = values.phone_input.startsWith("+")
        ? values.phone_input
        : `+${values.phone_input}`;

      const isPhoneNumberValid = isPossiblePhoneNumber(phoneToValidate);

      if (!isPhoneNumberValid) {
        setPhoneError("Please enter a valid phone number");
        return;
      }
    }

    setPhoneError("");

    try {
      // Build selected_tools_required_info from dynamic form fields
      const selectedTools = values.intervention_tools || [];
      const canonicalSelectedTools = selectedTools.map((toolName) => getCanonicalToolKey(toolName));
      const toolRequiredInfo = selectedTools.map((toolName) => {
        const toolKey = getCanonicalToolKey(toolName);

        return {
          tool: toolKey,
          required_info: values[`tool_required_info_${toolKey}`] || values[`tool_required_info_${toolName}`] || [],
          reply_message: form.getFieldValue(`tool_reply_message_${toolKey}`)?.trim() || "We will contact you soon!",
          allow_empty_response: form.getFieldValue(`tool_allow_empty_response_${toolKey}`) ?? false,
          block_reply_until_resolved: form.getFieldValue(`tool_block_reply_until_resolved_${toolKey}`) ?? false,
        };
      });

      // Prepare API data in the format expected by editBusinessInfoApi (same as tenant setup)
      const apiData = {
        org_name: values.org_name || "",
        org_description: values.org_description || "",
        contact_number: values.phone_input || "",
        org_email: values.org_email || "",
        agent_name: values.agent_name || "AI Bot",
        agent_goal_type: values.agent_goal_type || "",
        additional_agent_goal: values.additional_agent_goal || "",
        language_to_reply: (() => {
          const selectedLanguages = values.language || ["Auto Detect"];
          if (!primaryLanguage || !selectedLanguages.includes(primaryLanguage)) {
            return selectedLanguages;
          }
          // Place primary language first
          const otherLanguages = selectedLanguages.filter(lang => lang !== primaryLanguage);
          return [primaryLanguage, ...otherLanguages];
        })(),
        romanize_reply: values.romanize_reply || false,
        response_style: values.prompt || "standard",
        selected_tools: canonicalSelectedTools,
        selected_tools_required_info: toolRequiredInfo,
      };

      console.log("Saving AI setup data:", apiData);

      // Return a Promise that resolves when the API call completes
      return new Promise((resolve, reject) => {
        dispatch(
          editBusinessInfoApi({
            params: apiData,
            finalCallback: () => resolve(),
            successCallback: (response) => {
              console.log("AI setup update success:", response);
              message.success("AI setup updated successfully!");
            },
            failureCallback: (error) => {
              console.error("AI setup update error:", error);
              message.error("Failed to save AI setup. Please try again.");
              reject(error);
            },
          })
        );
      });
    } catch (error) {
      console.error("Error saving setup data:", error);
      message.error("Failed to save setup data");
      throw error;
    }
  };

  // Individual save functions for each section
  const handleSaveOrgInfo = async () => {
    setSavingOrgInfo(true);
    try {
      await handleSave(form.getFieldsValue());
    } catch (error) {
      // Error handling is already done in handleSave
    } finally {
      setSavingOrgInfo(false);
    }
  };

  const handleSaveAgentSetup = async () => {
    setSavingAgentSetup(true);
    try {
      await handleSave(form.getFieldsValue());
    } catch (error) {
      // Error handling is already done in handleSave
    } finally {
      setSavingAgentSetup(false);
    }
  };

  const handleSaveCtaConfig = async () => {
    setSavingCtaConfig(true);
    try {
      const values = form.getFieldsValue();

      await handleSave(values);
    } catch (error) {
      // Error handling is already done in handleSave
    } finally {
      setSavingCtaConfig(false);
    }
  };

  const handleCreateCustomTool = async (values) => {
    setCreatingCustomTool(true);
    try {
      await new Promise((resolve, reject) => {
        dispatch(
          createCustomToolApi({
            params: values,
            finalCallback: () => resolve(),
            successCallback: () => {
              message.success("Custom tool created successfully!");
              setShowCustomToolModal(false);
            },
            failureCallback: (error) => {
              message.error("Failed to create custom tool. Please try again.");
              reject(error);
            },
          })
        );
      });
      await fetchCustomTools();
    } catch (error) {
      console.error("Error creating custom tool:", error);
    } finally {
      setCreatingCustomTool(false);
    }
  };

  const handleDeleteCustomTool = async (toolName) => {
    try {
      await new Promise((resolve, reject) => {
        dispatch(
          deleteCustomToolApi({
            toolId: toolName,
            finalCallback: () => resolve(),
            successCallback: () => {
              message.success("Custom tool deleted successfully!");
            },
            failureCallback: (error) => {
              message.error("Failed to delete custom tool. Please try again.");
              reject(error);
            },
          })
        );
      });
      await fetchCustomTools();
    } catch (error) {
      console.error("Error deleting custom tool:", error);
    }
  };

  const handleEditCustomTool = async (tool) => {
    setCustomToolMode("edit");
    setEditingToolId(tool.issue_type || tool.name);
    setShowCustomToolModal(true);
  };

  const handleUpdateCustomTool = async (values) => {
    if (!editingToolId) return;
    setEditingCustomTool(true);
    try {
      await new Promise((resolve, reject) => {
        dispatch(
          updateCustomToolApi({
            toolId: editingToolId,
            params: values,
            finalCallback: () => resolve(),
            successCallback: () => {
              message.success("Custom tool updated successfully!");
              setShowCustomToolModal(false);
              setCustomToolMode("create");
              setEditingToolId(null);
              fetchCustomTools();
            },
            failureCallback: (error) => {
              message.error("Failed to update custom tool. Please try again.");
              reject(error);
            },
          })
        );
      });
    } catch (error) {
      console.error("Error updating custom tool:", error);
    } finally {
      setEditingCustomTool(false);
    }
  };

  const updateToolsWithLogic = (selectedTools) => {
    // Get default tools
    const defaultTools = toolsList.filter(t => t.default).map(t => t.issue_type || t.name);
    const nonDefaultTools = selectedTools.filter(t => !defaultTools.includes(t));

    // Only auto-include default tools if there are non-default tools selected
    const allTools = nonDefaultTools.length > 0
      ? [...new Set([...defaultTools, ...selectedTools])]
      : selectedTools;

    // Sort to show default tools first
    const sortedTools = allTools.sort((a, b) => {
      const aIsDefault = toolsList.find(t => (t.issue_type || t.name) === a)?.default;
      const bIsDefault = toolsList.find(t => (t.issue_type || t.name) === b)?.default;
      if (aIsDefault && !bIsDefault) return -1;
      if (!aIsDefault && bIsDefault) return 1;
      return 0;
    });

    // Update form value with sorted tools
    form.setFieldValue('intervention_tools', sortedTools);

    // Clear any dynamic required info fields for deselected tools
    const currentValues = form.getFieldsValue();
    Object.keys(currentValues)
      .filter((k) => k.startsWith('tool_required_info_'))
      .forEach((k) => {
        const toolKey = k.replace('tool_required_info_', '');
        if (!sortedTools.includes(toolKey)) {
          form.setFieldValue(k, []);
          form.setFieldValue(`tool_reply_message_${toolKey}`, "");
          form.setFieldValue(`tool_allow_empty_response_${toolKey}`, false);
          form.setFieldValue(`tool_block_reply_until_resolved_${toolKey}`, false);
        }
      });

    // Set defaults for any selected tools missing values
    sortedTools.forEach((toolKey) => {
      const dynKey = `tool_required_info_${toolKey}`;
      const val = form.getFieldValue(dynKey);
      if (!val || val.length === 0) {
        const tool = toolsList.find((item) => (item.issue_type || item.name) === toolKey);
        const customDefaults = tool?.required_fields?.length
          ? tool.required_fields
          : tool?.custom && tool?.webhook_params?.length
            ? tool.webhook_params.map((param) => param.field).filter(Boolean)
            : null;
        form.setFieldValue(dynKey, customDefaults || ["name", "contact_number"]);
      }
      if (form.getFieldValue(`tool_allow_empty_response_${toolKey}`) === undefined) {
        form.setFieldValue(`tool_allow_empty_response_${toolKey}`, false);
      }
      if (form.getFieldValue(`tool_block_reply_until_resolved_${toolKey}`) === undefined) {
        form.setFieldValue(`tool_block_reply_until_resolved_${toolKey}`, false);
      }
    });
  };

  const handleToolSelection = (toolId, checked) => {
    const tool = toolsList.find((t) => (t.issue_type || t.name) === toolId);
    if (!tool) return;

    const currentSelected = form.getFieldValue('intervention_tools') || [];

    if (checked) {
      // Selecting
      if (tool.is_pro && !isPaidPlan) {
        setShowUpgradeDialog(true);
        return;
      }

      // Add tool
      const newSelected = [...currentSelected, toolId];
      updateToolsWithLogic(newSelected);
    } else {
      // Deselecting
      if (tool.default) {
        const defaultTools = toolsList.filter(t => t.default).map(t => t.issue_type || t.name);
        const nonDefaultTools = currentSelected.filter(t => !defaultTools.includes(t));

        if (nonDefaultTools.length > 0) {
          message.error('Default tools cannot be removed when other tools are selected');
          return;
        }
      }

      const newSelected = currentSelected.filter(t => t !== toolId);
      updateToolsWithLogic(newSelected);
    }
  };

  return (
    <div className="ai-setup-component">
      <Card
        style={{
          width: "100%",
          borderRadius: 6,
          boxShadow: "0 1px 4px rgba(0, 0, 0, 0.05)",
        }}
        bodyStyle={{
          padding: 24,
          width: "100%",
        }}
      >
        {/* <Skeleton loading={loading} active paragraph={{ rows: 8 }}> */}
        <div
          style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
        >
          <Title
            level={4}
            style={{ margin: 0, fontWeight: 600 }}
          >
            AI Settings
          </Title>
        </div>

        <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
          Manage your AI assistant settings and business information
        </Text>

        <Divider style={{ margin: "0 0 24px 0" }} />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          style={{ width: "100%" }}
        >
          {/* Organization Information Section */}
          <Card
            title={
              <Space>
                <BankOutlined style={{ color: "#1890ff" }} />
                <Text strong style={{ fontSize: 16 }}>Organization Information</Text>
              </Space>
            }
            style={{ marginBottom: 24, border: "1px solid #f0f0f0" }}
            bodyStyle={{
              padding: "20px",
            }}
          >
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="org_name"
                  label="Organization Name"
                  required
                  rules={[
                    {
                      required: true,
                      message: "Please enter organization name",
                    },
                  ]}
                >
                  <Input placeholder="Enter your organization name" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name="org_email"
                  label="Organization Email"
                  rules={[
                    {
                      type: "email",
                      message: "Please enter a valid email address",
                    },
                  ]}
                >
                  <Input
                    type="email"
                    placeholder="Enter your organization email"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name="phone_input"
                  label="Organization Contact Number"
                  validateStatus={phoneError ? "error" : ""}
                  help={phoneError || ""}
                >
                  <style>
                    {`
                        .country-search {
                          padding: 0 !important;
                          margin: 0;
                          height: 40px;               /* or your desired height */
                          display: flex;
                          align-items: center;
                        }

                        .country-search-box {
                          width: 100%;
                          height: 100%;
                          // padding: 0 12px;
                          border: none;
                          outline: none;
                          box-sizing: border-box;
                          margin-left: 0 !important;

                          /* Optional */
                          background: transparent;
                        }
                      `}
                  </style>
                  <PhoneInput
                    country={"np"}
                    enableSearch={true}
                    inputClass="form-input"
                    containerClass="phone-input-container"
                    buttonClass="country-dropdown"
                    searchClass="country-search"
                    dropdownClass="country-dropdown-list"
                    value={
                      phoneInputValue ||
                      form.getFieldValue("phone_input") ||
                      ""
                    }
                    inputStyle={{
                      width: "100%",
                      height: "40px",
                      fontSize: "14px",
                      fontWeight: "400",
                      borderRadius: "0 8px 8px 0",
                    }}
                    buttonStyle={{
                      borderRadius: "8px 0 0 8px",
                      borderRight: "none",
                      backgroundColor: "#fafafa",
                    }}
                    onChange={(phone, countryData) => {
                      setPhoneError("");
                      setPhoneInputValue(phone);
                      const countryCode = countryData.dialCode
                        ? `+${countryData.dialCode}`
                        : "";
                      const fullNumber = phone;
                      const phoneNumberOnly = countryData.dialCode
                        ? fullNumber.replace(countryData.dialCode, "")
                        : fullNumber;
                      const cleanedPhoneNumber = phoneNumberOnly.replace(
                        /\D/g,
                        ""
                      );

                      form.setFieldsValue({
                        country_code: countryCode,
                        phone_number: cleanedPhoneNumber,
                        phone_input: phone,
                      });
                    }}
                    preferredCountries={["in", "us", "np", "gb"]}
                    autoFormat={true}
                    placeholder="Enter organization contact number"
                    disableSearchIcon={true}
                    searchPlaceholder="Search country"
                  />
                </Form.Item>

                {/* Hidden fields for country code and phone number */}
                <Form.Item name="country_code" hidden={true}>
                  <Input />
                </Form.Item>

                <Form.Item name="phone_number" hidden={true}>
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  name="org_description"
                  label="Organization Description"
                >
                  <TextArea
                    rows={3}
                    placeholder="Describe your organization..."
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Organization Info Save Button */}
            <div style={{ textAlign: "right", marginTop: 16, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
              <Button
                type="primary"
                onClick={handleSaveOrgInfo}
                loading={savingOrgInfo}
                icon={<SaveOutlined />}
                style={{
                  borderRadius: "6px",
                  height: "36px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Save Organization Info
              </Button>
            </div>
          </Card>

          {/* AI Agent Setup Section */}
          <Card
            title={
              <Space>
                <BulbOutlined style={{ color: "#1890ff" }} />
                <Text strong style={{ fontSize: 16 }}>AI Agent Settings</Text>
              </Space>
            }
            style={{ marginBottom: 24, border: "1px solid #f0f0f0" }}
            bodyStyle={{ padding: "20px" }}
          >
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="agent_name"
                  label="Agent Name"
                  required
                  rules={[
                    {
                      required: true,
                      message: "Please enter the agent name",
                    },
                  ]}
                >
                  <Input placeholder="Enter agent name" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <span style={{ marginRight: 8 }}>Language to Reply In</span>
                  {/* <Form.Item name="romanize_reply" valuePropName="checked" noStyle>
                    <Checkbox>
                      Romanize Reply
                      <Tooltip title="Enable this to romanize the AI's responses (e.g., convert non-Latin scripts to Latin characters)">
                        <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                      </Tooltip>
                    </Checkbox>
                  </Form.Item> */}
                </div>
                <Form.Item
                  name="language"
                  rules={[
                    { required: true, message: "Please select languages" },
                  ]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select languages"
                    style={{ width: "100%" }}
                    value={form.getFieldValue("language") || []}
                    onChange={(selected) => {
                      if (
                        selected.includes("Auto Detect") &&
                        selected.length > 1
                      ) {
                        if (selected[selected.length - 1] === "Auto Detect") {
                          form.setFieldsValue({ language: ["Auto Detect"] });
                          setPrimaryLanguage(null);
                        } else {
                          const filteredSelected = selected.filter(
                            (v) => v !== "Auto Detect"
                          );
                          form.setFieldsValue({ language: filteredSelected });
                          // If primary was removed, reset it
                          if (!filteredSelected.includes(primaryLanguage)) {
                            setPrimaryLanguage(filteredSelected[0] || null);
                          }
                        }
                      } else {
                        form.setFieldsValue({ language: selected });
                        // If primary was removed, reset it
                        if (!selected.includes(primaryLanguage)) {
                          setPrimaryLanguage(selected[0] || null);
                        }
                      }
                    }}
                    showSearch
                    optionFilterProp="children"
                    dropdownStyle={{ minWidth: 200 }}
                    tagRender={(props) => {
                      const { label, value, closable, onClose } = props;
                      const selectedLanguages = form.getFieldValue("language") || [];
                      const isPrimary = value === primaryLanguage;
                      const showPrimaryOption = selectedLanguages.length > 1 &&
                        value !== "Auto Detect" &&
                        selectedLanguages.includes(value);

                      return (
                        <Tag
                          color={isPrimary ? 'blue' : 'default'}
                          closable={closable}
                          onClose={onClose}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginRight: 4,
                            marginBottom: 4,
                            marginTop: 4,
                          }}
                        >
                          {label}
                          {showPrimaryOption && !isPrimary && (
                            <Tooltip title="The primary language will be used to respond when the user sends a message in a language other than the selected ones.">
                              <Button
                                size="small"
                                type="link"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setPrimaryLanguage(value);
                                }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                style={{
                                  marginLeft: 8,
                                  fontSize: 12,
                                  color: '#1890ff',
                                  cursor: 'pointer',
                                }}
                              >
                                Set Primary
                              </Button>
                            </Tooltip>
                          )}
                          {isPrimary && <CheckOutlined style={{ marginLeft: 8 }} />}
                        </Tag>
                      );
                    }}
                  >
                    <OptGroup label="Smart Options">
                      <Option value="Auto Detect">🌐 Auto Detect</Option>
                    </OptGroup>
                    <OptGroup label="Languages">
                      {languages.map((lang) => (
                        <Option key={lang} value={lang}>
                          {lang}
                        </Option>
                      ))}
                    </OptGroup>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name="agent_goal_type"
                  label={<span>Agent Goal</span>}
                  rules={[
                    {
                      required: true,
                      message: "Please select an agent goal type",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select agent goal type"
                    style={{ width: "100%" }}
                  >
                    {agentGoalTypes.map((goal) => (
                      <Option key={goal} value={goal}>
                        {goal}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name="prompt"
                  label="Response Style"
                  rules={[
                    {
                      required: true,
                      message: "Please select a response style",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select response style"
                    style={{ width: "100%" }}
                  >
                    {responseStyles.map((style) => (
                      <Option key={style} value={style}>
                        {style}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  name="additional_agent_goal"
                  label="Additional Agent Instructions"
                >
                  <TextArea
                    rows={3}
                    placeholder="Describe any additional goals or behaviors for your AI agent..."
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* AI Agent Setup Save Button */}
            <div style={{ textAlign: "right", marginTop: 16, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
              <Button
                type="primary"
                onClick={handleSaveAgentSetup}
                loading={savingAgentSetup}
                icon={<SaveOutlined />}
                style={{
                  borderRadius: "6px",
                  height: "36px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Save AI Agent Settings
              </Button>
            </div>
          </Card>

          {/* AI Assistant Settings Section */}
          <Card
            id="ai-settings-section"
            title={
              <Space>
                <SettingOutlined style={{ color: "#1890ff" }} />
                <Text strong style={{ fontSize: 16 }}>AI Assistant Settings</Text>
              </Space>
            }
            style={{ marginBottom: 24, border: "1px solid #f0f0f0" }}
            bodyStyle={{ padding: "20px" }}
          >
            <AISettingsComponent />
          </Card>

          {/* Call to Action Settings Section */}
          <Card
            title={
              <Space style={{ width: "100%", justifyContent: "space-between" }}>
                <Space>
                  <SettingOutlined style={{ color: "#1890ff" }} />
                  <Text strong style={{ fontSize: 16 }}>Follow-up Tools Settings</Text>
                </Space>
                <Button
                  size="small"
                  type="link"
                  icon={<SettingOutlined />}
                  onClick={() => setShowCustomToolModal(true)}
                >
                  Advanced Settings
                </Button>
              </Space>
            }
            style={{ marginBottom: 24, border: "1px solid #f0f0f0" }}
            bodyStyle={{ padding: "20px" }}
          >
            <div style={{ padding: 0 }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                Select the tools your agent can use to follow up with customers. Configure required information for each enabled tool.
              </Text>

              {/* Hidden field to maintain form state */}
              <Form.Item name="intervention_tools" hidden>
                <Select mode="multiple" />
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) => prev.intervention_tools !== curr.intervention_tools}
              >
                {({ getFieldValue }) => {
                  const selectedTools = getFieldValue('intervention_tools') || [];

                  // Sort tools for display: Default first, then alphabetical or by original order
                  const sortedDisplayTools = [...toolsList].sort((a, b) => {
                    if (a.default && !b.default) return -1;
                    if (!a.default && b.default) return 1;
                    return 0;
                  });

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {sortedDisplayTools.map((tool) => {
                        const toolId = tool.issue_type || tool.name;
                        const isSelected = selectedTools.includes(toolId);
                        const requiredInfoOptions = tool.custom && tool.webhook_params?.length
                          ? tool.webhook_params.map((param) => ({
                            name: param.field,
                            label: param.field,
                          }))
                          : requiredInfoSuggestions;

                        return (
                          <div
                            key={toolId}
                            style={{
                              border: `1px solid ${isSelected ? '#1890ff' : '#d9d9d9'}`,
                              borderRadius: 8,
                              background: isSelected ? '#f0f5ff' : '#fff',
                              transition: 'all 0.3s',
                              overflow: 'auto'
                            }}
                          >
                            {/* Header */}
                            <div
                              style={{
                                padding: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer'
                              }}
                              onClick={() => handleToolSelection(toolId, !isSelected)}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {(() => {
                                  const defaultToolIds = toolsList.filter(t => t.default).map(t => t.issue_type || t.name);
                                  const nonDefaultSelectedTools = selectedTools.filter(t => !defaultToolIds.includes(t));
                                  const isDefaultAndLocked = tool.default && isSelected && nonDefaultSelectedTools.length > 0;

                                  const switchElement = (
                                    <Switch
                                      checked={isSelected}
                                      disabled={isDefaultAndLocked}
                                      onClick={(_, e) => e.stopPropagation()}
                                      onChange={(checked) => handleToolSelection(toolId, checked)}
                                    />
                                  );

                                  if (isDefaultAndLocked) {
                                    return (
                                      <Tooltip title="Default tools cannot be disabled while other tools are enabled">
                                        {switchElement}
                                      </Tooltip>
                                    );
                                  }
                                  return switchElement;
                                })()}
                                <div>
                                  <Text strong style={{ fontSize: 16 }}>{tool.label}</Text>
                                  {tool.description && (
                                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{tool.description}</div>
                                  )}
                                </div>
                              </div>

                              <Space>
                                {tool.default && (
                                  <Tooltip title="Default Tool">
                                    <Tag color="blue">Default</Tag>
                                  </Tooltip>
                                )}
                                {tool.custom && (
                                  <Tooltip title="Custom Tool">
                                    <Tag color="purple">Custom</Tag>
                                  </Tooltip>
                                )}
                                {tool.is_pro && !isPaidPlan && (
                                  <Tag color="gold">PRO</Tag>
                                )}
                                {tool.custom && (
                                  <Button
                                    type="text"
                                    icon={<EditOutlined />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditCustomTool(tool);
                                    }}
                                    size="large"
                                    style={{ color: "#1890ff" }}
                                  />
                                )}

                                {tool.custom && (
                                  <Popconfirm
                                    title="Are you sure you want to delete this tool?"
                                    onConfirm={(e) => {
                                      e.stopPropagation();
                                      handleDeleteCustomTool(toolId);
                                    }}
                                    onCancel={(e) => e.stopPropagation()}
                                    okText="Yes"
                                    cancelText="No"
                                  >
                                    <Button
                                      type="text"
                                      danger
                                      icon={<DeleteOutlined />}
                                      onClick={(e) => e.stopPropagation()}
                                      size="large"
                                    />
                                  </Popconfirm>
                                )}
                              </Space>
                            </div>

                            {/* Expanded Config */}
                            {isSelected && (
                              <div style={{
                                padding: '0 16px 16px 16px',
                                borderTop: '1px solid rgba(0,0,0,0.06)',
                                marginTop: 8,
                                paddingTop: 16,
                                background: '#fff'
                              }}>
                                <Form.Item
                                  name={`tool_required_info_${toolId}`}
                                  label={<span>Required Information for {tool.label}</span>}
                                  style={{ marginBottom: 16 }}
                                >
                                  <Select
                                    mode="tags"
                                    style={{ width: '100%' }}
                                    placeholder={`Select required info for ${tool.label}`}
                                  >
                                    {requiredInfoOptions.map((s) => (
                                      <Option key={s.name || s} value={s.name || s}>
                                        {s.label || s}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                                <Row gutter={[16, 12]}>
                                  <Col xs={24} sm={12}>
                                    <Space>
                                      <Form.Item
                                        name={`tool_allow_empty_response_${toolId}`}
                                        valuePropName="checked"
                                        initialValue={false}
                                        noStyle
                                      >
                                        <Switch
                                          size="small"
                                          onChange={(checked) => {
                                            if (checked) {
                                              form.setFields([{ name: `tool_reply_message_${toolId}`, errors: [] }]);
                                            }
                                          }}
                                        />
                                      </Form.Item>
                                      <Text>Allow Empty Response</Text>
                                      <Tooltip title="If enabled, system won't reply to customer after creating this ticket.">
                                        <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                                      </Tooltip>
                                    </Space>
                                  </Col>
                                  <Col xs={24} sm={12}>
                                    <Space>
                                      <Form.Item
                                        name={`tool_block_reply_until_resolved_${toolId}`}
                                        valuePropName="checked"
                                        initialValue={false}
                                        noStyle
                                      >
                                        <Switch size="small" />
                                      </Form.Item>
                                      <Text>Block Reply Until Resolved</Text>
                                      <Tooltip title="If enabled, AI won't reply to the customer until the ticket is resolved.">
                                        <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                                      </Tooltip>
                                    </Space>
                                  </Col>
                                  <Col xs={24}>
                                    <Form.Item
                                      noStyle
                                      shouldUpdate={(prev, curr) =>
                                        prev[`tool_allow_empty_response_${toolId}`] !== curr[`tool_allow_empty_response_${toolId}`]
                                      }
                                    >
                                      {({ getFieldValue }) => {
                                        const allowEmptyResponse = getFieldValue(`tool_allow_empty_response_${toolId}`) || false;

                                        return !allowEmptyResponse ? (
                                          <Form.Item
                                            name={`tool_reply_message_${toolId}`}
                                            label={
                                              <Space>
                                                <span>Reply Message</span>
                                                <Tooltip title="This message will be sent to user when allow empty message is disabled.">
                                                  <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                                                </Tooltip>
                                              </Space>
                                            }
                                            style={{ marginBottom: 0, marginTop: 16 }}
                                          >
                                            <TextArea
                                              rows={3}
                                              placeholder={`Enter reply message for ${tool.label}`}
                                            />
                                          </Form.Item>
                                        ) : null;
                                      }}
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
              </Form.Item>
            </div>

            {/* CTA Settings Save Button */}
            <div style={{ textAlign: "right", marginTop: 16, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
              <Button
                type="primary"
                onClick={handleSaveCtaConfig}
                loading={savingCtaConfig}
                icon={<SaveOutlined />}
                style={{
                  borderRadius: "6px",
                  height: "36px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Save Follow-up Tools Settings
              </Button>
            </div>
          </Card>
        </Form>
        {/* </Skeleton> */}
      </Card>

      <UpgradeDialog
        visible={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        feature="This follow-up tool"
      />

      <CustomToolModal
        visible={showCustomToolModal}
        onCreate={customToolMode === "edit" ? handleUpdateCustomTool : handleCreateCustomTool}
        onCancel={() => {
          setShowCustomToolModal(false);
          setCustomToolMode("create");
          setEditingToolId(null);
        }}
        loading={creatingCustomTool || editingCustomTool}
        mode={customToolMode}
        initialValues={customToolMode === "edit" && editingToolId ? customTools.find(t => (t.issue_type || t.name) === editingToolId) : null}
      />
    </div>
  );
};

export default AISetupComponent;